// Cloudflare Pages + Workers on same domain - use relative paths

export function getErrorMessage(err: unknown, fallback: string): string {
  if (err && typeof err === "object") {
    const o = err as Record<string, unknown>;
    const data = o.data;
    if (data && typeof data === "object" && data !== null && "error" in data) {
      const errorVal = (data as Record<string, unknown>).error;
      if (typeof errorVal === "string") return errorVal;
    }
    if ("message" in o && typeof o.message === "string") return o.message;
  }
  if (err instanceof Error) return err.message;
  return fallback;
}

export async function fetchApi<T>(
  url: string,
  options?: RequestInit,
): Promise<T> {
  try {
    // Add timeout to prevent hanging (20 seconds)
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 20000);

    const response = await fetch(url, {
      ...options,
      signal: controller.signal,
      credentials: "include",
      headers: {
        "Content-Type": "application/json",
        ...options?.headers,
      },
    });

    clearTimeout(timeoutId);

    if (!response.ok) {
      let errorMessage = "Unknown error";
      let errorData: Record<string, unknown> | null = null;
      let responseText = "";

      try {
        responseText = await response.text();
        // Try to parse as JSON
        try {
          errorData = JSON.parse(responseText);
          const err = errorData?.error;
          const msg =
            (typeof err === "string" ? err : null) ??
            (err && typeof err === "object" && "message" in err
              ? String((err as Record<string, unknown>).message ?? "")
              : null) ??
            (err && typeof err === "object" && "details" in err
              ? String((err as Record<string, unknown>).details ?? "")
              : null) ??
            (typeof errorData?.message === "string" ? errorData.message : null);
          errorMessage =
            (msg && msg.length > 0 ? msg : null) ??
            `HTTP ${response.status}: ${response.statusText}`;
        } catch {
          // Not JSON, use text
          errorMessage =
            responseText || `HTTP ${response.status}: ${response.statusText}`;
        }
      } catch {
        errorMessage = `HTTP ${response.status}: ${response.statusText}`;
      }

      const error = new Error(errorMessage) as Error & {
        status?: number;
        data?: Record<string, unknown> | null;
        responseText?: string;
      };
      error.status = response.status;
      error.data = errorData;
      error.responseText = responseText;
      throw error;
    }

    return response.json();
  } catch (err) {
    // Handle abort/timeout errors
    if (err instanceof Error && err.name === "AbortError") {
      throw new Error(
        "Request timeout: The server took too long to respond. Please try again.",
      );
    }
    // Handle network errors
    if (err instanceof TypeError && err.message.includes("fetch")) {
      throw new Error(
        "Network error: Unable to connect to server. Please check your connection.",
      );
    }
    throw err;
  }
}
